import {
  type Campaign,
  CampaignCreatorsActiveStatusFilter,
  type CampaignDetailsCreator,
  type CampaignStats,
  type CampaignStatusEnum,
  type OfferResourceInclude,
} from '@metapic-apps/shared'
import { type MaybeRefOrGetter, type Ref, ref, toValue, watch, watchEffect } from 'vue'

import { handleErrorMessage } from '@/lib'
import { normalizeCampaignCreatorsList } from '@/lib/adapters/campaign-creators.adapter'
import { normalizeCampaignStats } from '@/lib/adapters/campaign-performance.adapter'
import { useApi } from '@/lib/helpers/api.helpers'
import { request } from '@/lib/services'
import { metapicApiClient } from '@/lib/services/metapic-api.service'
import type { CampaignDetailsCreatorRaw } from '@/lib/types/campaign-creators.types'
import { useErrorStore } from '@/stores/error.stores'

export const useGetCampaign = (
  campaignId: number,
  options?: { includes?: MaybeRefOrGetter<OfferResourceInclude[]> },
) => {
  const data = ref<Campaign>()

  const { loading, error, getCampaign } = useApi({
    getCampaign: async () => {
      const includes = toValue(options?.includes)
      data.value = await metapicApiClient.userOffersShow(
        campaignId,
        includes?.length ? { 'include[]': includes } : undefined,
      )
    },
  })

  watchEffect(() => void getCampaign())

  return {
    data,
    getCampaign,
    isLoading: loading,
    error,
  }
}

export const useDeleteCampaign = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const deleteCampaign = async (campaignId: number) => {
    try {
      isLoading.value = true
      error.value = null

      await request.delete(`/v2/offers/${campaignId}`)
      isLoading.value = false
    } catch (e) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      isLoading.value = false
    } finally {
      isLoading.value = false
    }
  }

  return {
    isLoading,
    error,
    deleteCampaign,
  }
}

export const useSetCampaignActivityRequest = (campaignId: number) =>
  useApi({
    setStatus: async (shouldPause: boolean): Promise<CampaignStatusEnum | undefined> => {
      const url = shouldPause
        ? `/v2/offers/${campaignId}/pause`
        : `/v2/offers/${campaignId}/unpause`
      const { data } = await request.post(url, {})
      return data.status
    },
  })

export const useCampaignStatsRequest = (
  campaignId: number,
  dateRange: Ref<{ startDate: string; endDate: string }>,
) => {
  const data = ref<CampaignStats>()
  const { loading, fetchStats } = useApi({
    fetchStats: async ({ startDate, endDate }: { startDate: string; endDate: string }) => {
      const response = await request.get(`/v2/offers/${campaignId}/stats`, {
        params: { start_date: startDate, end_date: endDate },
      })
      data.value = normalizeCampaignStats(response.data)
    },
  })

  watch(dateRange, () => void fetchStats(dateRange.value), { immediate: true })

  return { data, isLoading: loading }
}

export const useGetCampaignCreatorsListRequest = ({
  id,
  page,
  status,
  searchQuery,
  sortBy,
  dateRange,
}: {
  id: number
  status: Ref<CampaignCreatorsActiveStatusFilter>
  page?: Ref<number>
  searchQuery?: Ref<string>
  sortBy: {
    field: Ref<string>
    order: Ref<'asc' | 'desc'>
  }
  dateRange: Ref<{ startDate: string; endDate: string }>
}) => {
  const data = ref<CampaignDetailsCreator[] | undefined>(undefined)
  const totalPages = ref(0)

  const { loading, fetchCreatorsList } = useApi({
    fetchCreatorsList: async () => {
      const statusVal = toValue(status)
      const pageVal = toValue(page)
      const searchQueryVal = toValue(searchQuery)
      const sortOrderVal = toValue(sortBy.order)
      const sortFieldVal = toValue(sortBy.field)
      const { startDate, endDate } = toValue(dateRange)

      const isActive = statusVal === CampaignCreatorsActiveStatusFilter.Active
      const response = await metapicApiClient.getCampaignParticipantStats(
        id,
        {
          active: isActive,
          start_date: startDate,
          end_date: endDate,
          ...(isActive && { sort_by: `${sortFieldVal}:${sortOrderVal}` }),
          ...(searchQueryVal && { query: searchQueryVal }),
        },
        { params: { ...(pageVal && { page: pageVal }) } },
      )
      // The generated schema types estimatedMediaValue as a string, but the API actually
      // returns an object with `.amount` — see CampaignDetailsCreatorRaw.
      data.value = normalizeCampaignCreatorsList(
        response.data as unknown as CampaignDetailsCreatorRaw[],
      )
      totalPages.value = response.meta.lastPage
    },
  })

  watch(
    [status, page, sortBy.order, sortBy.field, dateRange, searchQuery],
    () => void fetchCreatorsList(),
    { immediate: true },
  )

  return { data, isLoading: loading, totalPages }
}

export const useGetCampaignCreatorsCount = ({
  id,
  dateRange,
}: {
  id: number
  dateRange: Ref<{ startDate: string; endDate: string }>
}) => {
  const data = ref<{ activeUsers: number; inactiveUsers: number } | undefined>(undefined)

  const { loading, fetchCreatorsCount } = useApi({
    fetchCreatorsCount: async () => {
      const { startDate, endDate } = toValue(dateRange)
      const response = await metapicApiClient.getCampaignParticipantCountByClickActivity(id, {
        start_date: startDate,
        end_date: endDate,
      })
      data.value = response
    },
  })

  watch(dateRange, () => void fetchCreatorsCount(), { immediate: true })

  return { data, isLoading: loading }
}
